import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getSharedDashboard, getSharedDashboardData } from "../API";
import DashboardCharts, { formatMonthLabel } from "./DashboardCharts";
import { PROVINCES } from "../Constants";
import DropdownMultiSelect from "../components/DropdownMultiSelect";

function generateMonths(count) {
  const months = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString("en", { month: "long", year: "numeric" });
    months.push({ value, label });
  }
  return months;
}

const MONTH_OPTIONS = generateMonths(24);

export default function SharedDashboardReport() {
  const { token } = useParams();
  const [config, setConfig] = useState(null);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedProvinces, setSelectedProvinces] = useState([]);
  const [data, setData] = useState(null);
  const [initLoading, setInitLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    getSharedDashboard(token)
      .then((cfg) => {
        setConfig(cfg);
        setSelectedMonths(MONTH_OPTIONS.slice(0, 3));
        setInitLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setInitLoading(false);
      });
  }, [token]);

  useEffect(() => {
    if (!config || selectedMonths.length === 0) return;
    const timer = setTimeout(() => {
      setDataLoading(true);
      getSharedDashboardData(token, {
        months: selectedMonths.map((m) => m.value),
        provinceIds: selectedProvinces.map((p) => p.id),
        bannerIds: [],
      })
        .then(setData)
        .catch((e) => setError(e.message))
        .finally(() => setDataLoading(false));
    }, 500);
    return () => clearTimeout(timer);
  }, [config, selectedMonths, selectedProvinces, token]);

  if (initLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <div className="spinner-border text-secondary" role="status" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  const visibleCharts = new Set(config.visibleCharts);
  const monthsLabel = selectedMonths.length > 0
    ? `${formatMonthLabel(selectedMonths[selectedMonths.length - 1].value)} – ${formatMonthLabel(selectedMonths[0].value)}`
    : "";

  return (
    <div className="container-fluid py-4 px-4">
      <h5 className="mb-1">Distribution Dashboard</h5>
      {config.manufacturerName && <div className="text-muted small mb-3">{config.manufacturerName}</div>}

      <div className="card mb-4">
        <div className="card-body py-3">
          <div className="row g-2 align-items-end">
            <div className="col-md-4 col-lg-3">
              <label className="form-label small fw-semibold mb-1">Months</label>
              <DropdownMultiSelect
                options={MONTH_OPTIONS}
                value={selectedMonths}
                onChange={setSelectedMonths}
                getOptionLabel={(o) => o.label}
                getOptionValue={(o) => o.value}
                placeholder="Select months…"
              />
            </div>
            <div className="col-md-4 col-lg-3">
              <label className="form-label small fw-semibold mb-1">Provinces</label>
              <DropdownMultiSelect
                options={PROVINCES}
                value={selectedProvinces}
                onChange={setSelectedProvinces}
                getOptionLabel={(o) => o.name}
                getOptionValue={(o) => o.id}
                placeholder="All provinces"
              />
            </div>
            {dataLoading && (
              <div className="col-auto d-flex align-items-end pb-1">
                <span className="text-muted small">Loading…</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {monthsLabel && <div className="text-muted small mb-3">{monthsLabel}</div>}

      {data && <DashboardCharts data={data} visibleCharts={visibleCharts} />}
    </div>
  );
}
